import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;
const REASONS = [['objetivo_atingido', 'Objetivo atingido'], ['fim_programa', 'Fim de programa'], ['mudou_nutricionista', 'Mudou de nutricionista'], ['outro', 'Outro']];
const STATE_LABEL = { ativo: 'Ativa', alta: 'Alta', pausa: 'Pausa', terminou_sem_alta: 'Terminou sem alta', inativo: 'Inativa' };
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const btn = 'px-3 py-1.5 rounded-full text-xs font-medium border border-brand-border bg-white text-brand-text hover:bg-brand-bg';

function Modal({ children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3">{children}</div>
    </div>
  );
}

function ReviewDialog({ patient, action, defaultDays, onClose, onDone }) {
  const [reason, setReason] = useState('objetivo_atingido');
  const [reasonText, setReasonText] = useState('');
  const [date, setDate] = useState(action === 'alta' ? addDays(defaultDays) : addDays(30));
  const [message, setMessage] = useState('');
  const [msgEdited, setMsgEdited] = useState(false);
  const editedRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = useCallback(() => ({ action, reason, access_end_date: action === 'alta' ? date : undefined, return_date: action === 'pausa' ? date : undefined, message: msgEdited ? message : undefined }), [action, reason, date, message, msgEdited]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/api/patients/${patient.user_id}/followup/preview`, body());
        setPreview(data);
        if (!editedRef.current) setMessage(data.default_message || '');
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [patient.user_id, body, msgEdited]);

  const send = async (sendMessage) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/patients/${patient.user_id}/followup`, { ...body(), message, reason_text: reasonText, send_message: sendMessage });
      toast.success(sendMessage ? (data.email_sent ? 'Guardado e email enviado' : 'Guardado (o email não foi enviado)') : 'Guardado sem enviar mensagem');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };

  return (
    <Modal testid={`followup-review-${action}`}>
      <h3 className="font-heading text-lg text-brand-text">{action === 'alta' ? 'Dar alta' : 'Pausa'}: {patient.name}</h3>
      {action === 'alta' && (
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Motivo</Label>
          <select value={reason} onChange={e => setReason(e.target.value)} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm" data-testid="followup-reason">
            {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {reason === 'outro' && <Input value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="Descreve o motivo" className="mt-2 rounded-xl border-brand-border" data-testid="followup-reason-text" />}
        </div>
      )}
      <div>
        <Label className="text-xs text-brand-muted mb-1 block">{action === 'alta' ? 'Data de fim de acesso' : 'Data prevista de regresso'}</Label>
        <div className="flex gap-2 flex-wrap items-center">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-xl border-brand-border w-44" data-testid="followup-date" />
          {action === 'alta' && [15, 30, 60].map(n => <button key={n} onClick={() => setDate(addDays(n))} className={btn} data-testid={`followup-days-${n}`}>{n} dias</button>)}
        </div>
      </div>
      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Mensagem pessoal para a paciente (opcional)</Label>
        <Textarea value={message} onChange={e => { setMessage(e.target.value); setMsgEdited(true); editedRef.current = true; }} rows={3} className="rounded-xl border-brand-border" data-testid="followup-message" />
      </div>
      {preview && (
        <div className="border border-brand-border rounded-xl p-2 bg-brand-bg/40" data-testid="followup-preview">
          <p className="text-[11px] text-brand-muted mb-1">Assunto: {preview.subject}</p>
          <div className="bg-white rounded-lg text-xs" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}
      <div className="flex gap-2 justify-end flex-wrap">
        <button onClick={onClose} className={btn} data-testid="followup-cancel">Cancelar</button>
        <button disabled={busy} onClick={() => send(false)} className={btn} data-testid="followup-dont-send">Não enviar</button>
        <button disabled={busy} onClick={() => send(true)} className="px-4 py-1.5 rounded-full text-xs font-medium bg-brand-green text-white" data-testid="followup-send">Enviar</button>
      </div>
    </Modal>
  );
}

export default function FollowupControls({ patient, onChanged }) {
  const [settings, setSettings] = useState({ access_days_after_discharge: 30 });
  const [dialog, setDialog] = useState(null);
  const state = patient.followup_state || (patient.status === 'ativo' && patient.active_followup !== false ? 'ativo' : 'inativo');
  useEffect(() => { axios.get(`${API}/api/admin/followup-settings`).then(r => setSettings(r.data)).catch(() => {}); }, []);
  const simple = async (action) => {
    if (!window.confirm('Tem a certeza?')) return;
    try { await axios.post(`${API}/api/patients/${patient.user_id}/followup`, { action }); toast.success('Estado atualizado'); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const ask = (action) => { if (window.confirm('Tem a certeza?')) setDialog(action); };
  const toggleTest = async () => {
    try { await axios.put(`${API}/api/patients/${patient.user_id}/test-flag`, { is_test: !patient.is_test }); onChanged(); } catch (e) { toast.error('Erro'); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="followup-controls">
      <span className="text-[11px] px-2 py-1 rounded-full bg-brand-green/10 text-brand-green font-medium" data-testid="followup-state">{STATE_LABEL[state] || state}</span>
      {patient.access_end_date && <span className="text-[11px] text-brand-muted" data-testid="followup-access-end">Acesso até {patient.access_end_date.split('-').reverse().join('/')}</span>}
      {state === 'ativo' ? (
        <>
          <button onClick={() => ask('alta')} className={btn} data-testid="followup-alta-btn">Dar alta</button>
          <button onClick={() => ask('pausa')} className={btn} data-testid="followup-pausa-btn">Pausa</button>
          <button onClick={() => simple('terminou_sem_alta')} className={btn} data-testid="followup-terminou-btn">Terminou sem alta</button>
        </>
      ) : (
        <button onClick={() => simple('reativar')} className={btn} data-testid="followup-reativar-btn">Reativar</button>
      )}
      <label className="flex items-center gap-1 text-[11px] text-brand-muted cursor-pointer"><input type="checkbox" checked={!!patient.is_test} onChange={toggleTest} data-testid="followup-test-toggle" /> Conta de teste</label>
      {dialog && <ReviewDialog patient={patient} action={dialog} defaultDays={settings.access_days_after_discharge} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged(); }} />}
    </div>
  );
}
